import { useEffect } from "react";
import { Button, DialogContentText, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/system";
import { QrCodeOutlined } from "@mui/icons-material";
import DialogBase from "./DialogBase";
import { useQRScanner } from "./ScanQR/hooks/useQRScanner";
import { useDeviceClaim } from "./ScanQR/hooks/useDeviceClaim";
import { QRCamera } from "./ScanQR/components/QRCamera";
import { QRScanSuccess } from "./ScanQR/components/QRScanSuccess";
import { DeviceClaimForm } from "./ScanQR/components/DeviceClaimForm";
import { getDialogButtonStyles } from "./ScanQR/utils/styles";

/**
 * ScanQR Dialog Component - Handles QR code scanning and device claiming
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.handleClose - Function to close the dialog
 * @returns {JSX.Element} ScanQR dialog component
 */
export default function ScanQRCodeDialog(props) {
  const { open, handleClose } = props;
  const { t } = useTranslation("common");
  const theme = useTheme();

  // Device claiming functionality
  const {
    claimCode,
    setClaimCode,
    organisationID,
    setOrganisationID,
    organisations,
    claimDevice,
    resetForm,
    isClaimingDevice,
  } = useDeviceClaim(handleClose);

  // QR scanning functionality
  const { scanned, cameraError, videoReady, initializeCamera } = useQRScanner(
    open,
    setClaimCode
  );

  // Reset form when dialog opens, but only if we haven't scanned anything yet
  useEffect(() => {
    if (open) {
      // Only reset if we don't have a claim code (meaning no successful scan)
      if (!claimCode) {
        resetForm();
      }
    }
  }, [open, claimCode, resetForm]);

  return (
    <DialogBase
      open={open}
      onClose={handleClose}
      title={t("common.scan_qr_code")}
      icon={<QrCodeOutlined />}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={getDialogButtonStyles(theme, "outlined")}
          >
            {t("common.close")}
          </Button>

          <Button
            variant="contained"
            onClick={claimDevice}
            disabled={!claimCode.length || isClaimingDevice}
            sx={getDialogButtonStyles(theme, "contained")}
          >
            {isClaimingDevice ? t("common.adding") : t("common.add")}
          </Button>
        </>
      }
    >
      <DialogContentText sx={{ mb: 2, fontSize: "0.95rem" }}>
        {t("common.scan_intro")}
      </DialogContentText>

      {!scanned ? (
        <QRCamera
          onCameraInit={initializeCamera}
          videoReady={videoReady}
          scanned={scanned}
          cameraError={cameraError}
        />
      ) : (
        <QRScanSuccess />
      )}

      <DeviceClaimForm
        claimCode={claimCode}
        onClaimCodeChange={setClaimCode}
        organisationID={organisationID}
        onOrganisationChange={setOrganisationID}
        organisations={organisations}
      />

      <Divider />
    </DialogBase>
  );
}
